/**
 * Mock Data for Customer Communications and Contacts
 * 
 * This file contains comprehensive mock data for:
 * - Communications (emails, phone calls, portal messages)
 * - Contacts (various roles and relationships)
 * 
 * Data is designed to be realistic and demonstrate various business scenarios
 */

import type { CommunicationApi, ContactApi } from "@/types/api/customer-detail";

/**
 * Generate realistic timestamps spanning the last 6 months
 */
function getDateMonthsAgo(months: number, daysOffset: number = 0): string {
  const date = new Date();
  date.setMonth(date.getMonth() - months);
  date.setDate(date.getDate() + daysOffset);
  date.setHours(9 + Math.floor(Math.random() * 8), Math.floor(Math.random() * 60), 0, 0);
  return date.toISOString();
}

/**
 * Comprehensive Communications Mock Data
 * 12 diverse communication records spanning 6 months
 */
export const mockCommunications: CommunicationApi[] = [
  // Recent communications (last 2 weeks)
  {
    id: "COMM-2024-001",
    customerId: "1",
    customerName: "Acme Corporation",
    contactName: "Sarah Mitchell",
    type: "email",
    subject: "Payment Confirmation - Invoice #INV-2024-1847",
    content: "Dear Sarah,\n\nThank you for your recent payment of $45,250.00 received on March 15, 2024. This payment has been successfully applied to Invoice #INV-2024-1847.\n\nYour current account balance is now $127,450.00. If you have any questions regarding your account, please don't hesitate to contact us.\n\nBest regards,\nAccounts Receivable Team",
    status: "read",
    direction: "outbound",
    createdAt: getDateMonthsAgo(0, -3),
    createdBy: "Jennifer Adams",
    metadata: {
      emailTo: "sarah.mitchell@acmecorp.com",
      emailFrom: "ar@yourcompany.com",
    },
  },
  {
    id: "COMM-2024-002",
    customerId: "1",
    customerName: "Acme Corporation",
    contactName: "David Chen",
    type: "phone",
    subject: "Urgent: Overdue Invoice Discussion",
    content: "Spoke with David Chen regarding Invoice #INV-2024-1723 which is 45 days overdue ($32,500). David explained they are experiencing temporary cash flow constraints due to a delayed customer payment. He committed to paying 50% by end of this week and the remaining balance within 14 days. Follow-up scheduled for Friday to confirm first payment received.",
    status: "completed",
    direction: "outbound",
    createdAt: getDateMonthsAgo(0, -5),
    createdBy: "Michael Torres",
    metadata: {
      duration: "18 minutes",
    },
  },
  {
    id: "COMM-2024-003",
    customerId: "2",
    customerName: "Global Tech Industries",
    contactName: "Robert Williams",
    type: "portal",
    subject: "Query: Invoice Discrepancy - INV-2024-1891",
    content: "Customer submitted inquiry via customer portal regarding a pricing discrepancy on Invoice #INV-2024-1891. They noted that the agreed unit price was $125.00 but invoice shows $135.00 for 200 units. Investigating with sales team and will issue credit note if confirmed. Response sent within 2 hours acknowledging receipt and providing timeline for resolution.",
    status: "completed",
    direction: "inbound",
    createdAt: getDateMonthsAgo(0, -7),
    createdBy: "System - Customer Portal",
  },
  
  // 2-4 weeks ago
  {
    id: "COMM-2024-004",
    customerId: "3",
    customerName: "Mega Retail Group",
    contactName: "Emily Rodriguez",
    type: "email",
    subject: "Monthly Account Statement - February 2024",
    content: "Dear Emily,\n\nPlease find attached your account statement for February 2024.\n\nAccount Summary:\n- Opening Balance: $245,600.00\n- New Invoices: $89,450.00\n- Payments Received: $156,200.00\n- Closing Balance: $178,850.00\n\nOutstanding invoices breakdown:\n- Current: $98,450.00\n- 1-30 days: $52,300.00\n- 31-60 days: $28,100.00\n\nPlease review and contact us if you have any questions.\n\nBest regards,\nAccounts Team",
    status: "delivered",
    direction: "outbound",
    createdAt: getDateMonthsAgo(0, -14),
    createdBy: "Jennifer Adams",
    metadata: {
      emailTo: "emily.rodriguez@megaretail.com",
      emailFrom: "ar@yourcompany.com",
    },
  },
  {
    id: "COMM-2024-005",
    customerId: "1",
    customerName: "Acme Corporation",
    contactName: "Sarah Mitchell",
    type: "phone",
    subject: "Payment Plan Negotiation",
    content: "Productive conversation with Sarah Mitchell regarding restructuring payment terms for outstanding balance of $127,450. Agreed to modified payment plan: $40,000 due immediately, followed by three monthly installments of $29,150. Customer appreciates flexibility during their expansion phase. Payment plan agreement to be formalized via email and signed by both parties.",
    status: "completed",
    direction: "outbound",
    createdAt: getDateMonthsAgo(0, -18),
    createdBy: "Michael Torres",
    metadata: {
      duration: "32 minutes",
    },
  },
  
  // 1-2 months ago
  {
    id: "COMM-2024-006",
    customerId: "4",
    customerName: "Premier Manufacturing",
    contactName: "James Patterson",
    type: "email",
    subject: "Credit Limit Review Request",
    content: "Dear James,\n\nThank you for your request to increase your credit limit from $150,000 to $250,000.\n\nWe have reviewed your account history and are pleased to inform you that your request has been approved. Your payment history has been excellent with consistent on-time payments over the past 18 months.\n\nYour new credit limit of $250,000 is now active and will be reflected in your account immediately.\n\nThank you for your continued business.\n\nBest regards,\nCredit Management Team",
    status: "read",
    direction: "outbound",
    createdAt: getDateMonthsAgo(1, -5),
    createdBy: "Lisa Chen",
    metadata: {
      emailTo: "james.patterson@premiermfg.com",
      emailFrom: "credit@yourcompany.com",
    },
  },
  {
    id: "COMM-2024-007",
    customerId: "2",
    customerName: "Global Tech Industries",
    contactName: "Amanda Foster",
    type: "portal",
    subject: "Request: Copy of Invoice INV-2024-1654",
    content: "Customer requested duplicate copy of Invoice #INV-2024-1654 via customer portal. Original invoice was sent on January 15, 2024. Customer indicated they need it for their audit records. PDF copy automatically generated and sent to registered email address. Customer confirmed receipt and thanked us for quick response.",
    status: "completed",
    direction: "inbound",
    createdAt: getDateMonthsAgo(1, -12),
    createdBy: "System - Customer Portal",
  },
  {
    id: "COMM-2024-008",
    customerId: "5",
    customerName: "Sunrise Enterprises",
    contactName: "Thomas Anderson",
    type: "phone",
    subject: "Dispute Resolution - Quality Issue",
    content: "Received call from Thomas Anderson disputing Invoice #INV-2024-1589 ($18,750) due to quality issues with delivered products. Customer claims 15% of shipment was damaged. Escalated to operations manager for investigation. Arranged for quality inspection and agreed to hold payment pending resolution. Customer appreciates professional handling and expects resolution within 5 business days.",
    status: "completed",
    direction: "inbound",
    createdAt: getDateMonthsAgo(1, -20),
    createdBy: "Michael Torres",
    metadata: {
      duration: "25 minutes",
    },
  },
  
  // 2-4 months ago
  {
    id: "COMM-2024-009",
    customerId: "3",
    customerName: "Mega Retail Group",
    contactName: "Emily Rodriguez",
    type: "email",
    subject: "Quarterly Business Review - Q4 2023",
    content: "Dear Emily,\n\nThank you for attending our Quarterly Business Review meeting yesterday. As discussed, here's a summary of key points:\n\n- Total purchases in Q4 2023: $487,500\n- Average payment time: 28 days (excellent!)\n- Volume discount tier achieved: Gold (15%)\n- Projected Q1 2024 volume: $520,000\n\nAction items:\n1. Implement electronic invoicing (target: Feb 1)\n2. Review new product catalog\n3. Schedule Q1 2024 review for April\n\nThank you for your continued partnership.\n\nBest regards,\nAccount Management Team",
    status: "replied",
    direction: "outbound",
    createdAt: getDateMonthsAgo(2, -8),
    createdBy: "Jennifer Adams",
    metadata: {
      emailTo: "emily.rodriguez@megaretail.com",
      emailFrom: "accounts@yourcompany.com",
    },
  },
  {
    id: "COMM-2024-010",
    customerId: "1",
    customerName: "Acme Corporation",
    contactName: "David Chen",
    type: "email",
    subject: "Payment Terms Modification Confirmation",
    content: "Dear David,\n\nThis email confirms our phone conversation regarding the modification of payment terms for your account.\n\nNew Terms (Effective January 1, 2024):\n- Payment Terms: Net 45 days (previously Net 30)\n- Early Payment Discount: 2% if paid within 15 days\n- Credit Limit: Increased to $200,000\n\nThese terms reflect your excellent payment history and growing business relationship. Please sign and return the attached agreement.\n\nThank you for your business.\n\nBest regards,\nCredit Department",
    status: "read",
    direction: "outbound",
    createdAt: getDateMonthsAgo(3, -2),
    createdBy: "Lisa Chen",
    metadata: {
      emailTo: "david.chen@acmecorp.com",
      emailFrom: "credit@yourcompany.com",
    },
  },
  
  // 4-6 months ago
  {
    id: "COMM-2024-011",
    customerId: "4",
    customerName: "Premier Manufacturing",
    contactName: "James Patterson",
    type: "phone",
    subject: "New Account Setup - Welcome Call",
    content: "Welcome call with James Patterson to establish new account relationship. Discussed payment terms (Net 30), invoicing preferences (email PDF), and contact information. James prefers email for routine communications and phone for urgent matters. Confirmed initial credit limit of $150,000. Provided overview of customer portal features and online payment options. Customer expressed satisfaction with onboarding process.",
    status: "completed",
    direction: "outbound",
    createdAt: getDateMonthsAgo(5, -10),
    createdBy: "Jennifer Adams",
    metadata: {
      duration: "22 minutes",
    },
  },
  {
    id: "COMM-2024-012",
    customerId: "5",
    customerName: "Sunrise Enterprises",
    contactName: "Thomas Anderson",
    type: "portal",
    subject: "Feedback: Improved Payment Portal",
    content: "Customer submitted positive feedback via portal regarding recent improvements to online payment system. Specifically mentioned: faster load times, clearer invoice details, and easier payment allocation process. Suggested adding ability to schedule future payments. Feedback forwarded to IT development team for consideration in next release. Customer thanked for valuable input.",
    status: "completed",
    direction: "inbound",
    createdAt: getDateMonthsAgo(5, -25),
    createdBy: "System - Customer Portal",
  },
];

/**
 * Comprehensive Contacts Mock Data
 * 7 contact records with diverse roles and relationships
 */
export const mockContacts: ContactApi[] = [
  // Acme Corporation Contacts
  {
    id: "CONT-001",
    title: "Ms.",
    firstName: "Sarah",
    surname: "Mitchell",
    email: "sarah.mitchell@acmecorp.com",
    phone: "+1 (555) 234-5678",
    jobTitle: "Chief Financial Officer",
    company: "Acme Corporation",
    customerId: "1",
    notes: "Primary decision-maker for all financial matters. Prefers email communication for routine matters. Available for calls between 2-4 PM EST. Very responsive and professional. Has authority to approve payments up to $500,000 without additional sign-off.",
    createdAt: "2023-06-15T09:00:00Z",
    lastContact: getDateMonthsAgo(0, -3),
    isPrimary: true,
  },
  {
    id: "CONT-002",
    title: "Mr.",
    firstName: "David",
    surname: "Chen",
    email: "david.chen@acmecorp.com",
    phone: "+1 (555) 234-5679",
    jobTitle: "Accounts Payable Manager",
    company: "Acme Corporation",
    customerId: "1",
    notes: "Day-to-day contact for invoice processing and payment queries. Handles all routine AP operations. Reports to Sarah Mitchell. Best reached via email or phone between 9 AM - 5 PM EST. Processes invoices weekly on Thursdays. Requires PO numbers on all invoices.",
    createdAt: "2023-06-15T09:30:00Z",
    lastContact: getDateMonthsAgo(0, -5),
    isPrimary: false,
  },
  
  // Global Tech Industries Contacts
  {
    id: "CONT-003",
    title: "Mr.",
    firstName: "Robert",
    surname: "Williams",
    email: "robert.williams@globaltech.com",
    phone: "+1 (555) 345-6789",
    jobTitle: "Finance Director",
    company: "Global Tech Industries",
    customerId: "2",
    notes: "Primary contact for billing and payment matters. Oversees entire finance department. Prefers detailed monthly statements. Responds quickly to email inquiries. Authorized to approve all payments. Appreciates advance notice for large invoices. Works closely with procurement team.",
    createdAt: "2023-08-20T10:00:00Z",
    lastContact: getDateMonthsAgo(0, -7),
    isPrimary: true,
  },
  {
    id: "CONT-004",
    title: "Ms.",
    firstName: "Amanda",
    surname: "Foster",
    email: "amanda.foster@globaltech.com",
    phone: "+1 (555) 345-6790",
    jobTitle: "Senior Accountant",
    company: "Global Tech Industries",
    customerId: "2",
    notes: "Backup contact when Robert is unavailable. Handles invoice reconciliation and dispute resolution. Very detail-oriented and thorough. Prefers documentation via email. Available Monday-Friday 8 AM - 6 PM PST. Can escalate urgent matters to Robert Williams.",
    createdAt: "2023-08-20T10:30:00Z",
    lastContact: getDateMonthsAgo(1, -12),
    isPrimary: false,
  },
  
  // Mega Retail Group Contact
  {
    id: "CONT-005",
    title: "Ms.",
    firstName: "Emily",
    surname: "Rodriguez",
    email: "emily.rodriguez@megaretail.com",
    phone: "+1 (555) 456-7890",
    jobTitle: "VP of Finance & Operations",
    company: "Mega Retail Group",
    customerId: "3",
    notes: "Dual role overseeing both finance and operations. Primary contact for all account matters. Prefers quarterly business reviews. Excellent payment history. Interested in volume discounts and early payment incentives. Available for strategic discussions. Responds to emails within 24 hours.",
    createdAt: "2023-05-10T11:00:00Z",
    lastContact: getDateMonthsAgo(0, -14),
    isPrimary: true,
  },
  
  // Premier Manufacturing Contact
  {
    id: "CONT-006",
    title: "Mr.",
    firstName: "James",
    surname: "Patterson",
    email: "james.patterson@premiermfg.com",
    phone: "+1 (555) 567-8901",
    jobTitle: "Controller",
    company: "Premier Manufacturing",
    customerId: "4",
    notes: "Sole financial contact for the organisation. Manages all accounting functions. Prefers phone calls for urgent matters, email for routine communications. Processes payments bi-weekly on Wednesdays. Requires detailed backup documentation. Very organised and systematic. Appreciates proactive communication about upcoming invoices.",
    createdAt: "2023-10-01T09:00:00Z",
    lastContact: getDateMonthsAgo(1, -5),
    isPrimary: true,
  },
  
  // Sunrise Enterprises Contact
  {
    id: "CONT-007",
    title: "Mr.",
    firstName: "Thomas",
    surname: "Anderson",
    email: "thomas.anderson@sunriseent.com",
    phone: "+1 (555) 678-9012",
    jobTitle: "Owner & CEO",
    company: "Sunrise Enterprises",
    customerId: "5",
    notes: "Owner-operator who handles all financial decisions personally. Direct and straightforward communication style. Prefers phone calls over email. Available most days 7 AM - 7 PM CST. Values personal relationships and reliability. Sometimes needs flexible payment arrangements during seasonal cash flow variations. Loyal customer when treated fairly.",
    createdAt: "2023-07-15T10:00:00Z",
    lastContact: getDateMonthsAgo(1, -20),
    isPrimary: true,
  },
];

/**
 * Get communications for a specific customer
 */
export function getCommunicationsByCustomerId(customerId: string): CommunicationApi[] {
  return mockCommunications.filter(comm => comm.customerId === customerId);
}

/**
 * Get contacts for a specific customer
 */
export function getContactsByCustomerId(customerId: string): ContactApi[] {
  return mockContacts.filter(contact => contact.customerId === customerId);
}

/**
 * Get all communications (for admin/reporting views)
 */
export function getAllCommunications(): CommunicationApi[] {
  return mockCommunications;
}

/**
 * Get all contacts (for admin/reporting views)
 */
export function getAllContacts(): ContactApi[] {
  return mockContacts;
}

/**
 * Get primary contact for a customer
 */
export function getPrimaryContact(customerId: string): ContactApi | undefined {
  return mockContacts.find(contact => contact.customerId === customerId && contact.isPrimary);
}

/**
 * Get communication statistics for a customer
 */
export function getCommunicationStats(customerId: string) {
  const customerComms = getCommunicationsByCustomerId(customerId);
  
  return {
    total: customerComms.length,
    byType: {
      email: customerComms.filter(c => c.type === "email").length,
      phone: customerComms.filter(c => c.type === "phone").length,
      portal: customerComms.filter(c => c.type === "portal").length,
      chat: customerComms.filter(c => c.type === "chat").length,
      meeting: customerComms.filter(c => c.type === "meeting").length,
      sms: customerComms.filter(c => c.type === "sms").length,
      video: customerComms.filter(c => c.type === "video").length,
      note: customerComms.filter(c => c.type === "note").length,
    },
    byDirection: {
      inbound: customerComms.filter(c => c.direction === "inbound").length,
      outbound: customerComms.filter(c => c.direction === "outbound").length,
    },
    lastCommunication: customerComms.length > 0 ? customerComms[0].createdAt : null,
  };
}
